import React, { useState, useRef, useEffect } from 'react';
import { FaUserCircle, FaCaretDown, FaSignOutAlt, FaKey, FaEye, FaEyeSlash } from 'react-icons/fa';
import './UserDropdown.css';
import { config } from '../utils/config';

const UserDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const dropdownRef = useRef(null);

  // Get user info from localStorage
  const getUserInfo = () => {
    try {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        return JSON.parse(savedUser);
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
    return null;
  };

  const user = getUserInfo();

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleChangePassword = async () => {
    try {
      setPasswordLoading(true);
      setPasswordError('');

      // Validate passwords
      if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
        setPasswordError('All fields are required');
        setPasswordLoading(false);
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordError('New passwords do not match');
        setPasswordLoading(false);
        return;
      }


      // Get user data
      const savedUser = localStorage.getItem('user');
      const userData = savedUser ? JSON.parse(savedUser) : null;
      const token = userData?.token;

      // Try different possible login identifiers
      const loginIdentifier = userData?.login_identifier ||
        userData?.username ||
        userData?.email ||
        userData?.mobile_number ||
        userData?.id ||
        'superadmin'; // fallback for testing

      // For testing: if no token, use a mock one
      const authToken = token || 'mock-jwt-token-for-testing';

      if (!authToken) {
        setPasswordError('User not authenticated');
        setPasswordLoading(false);
        return;
      }

      // Debug: Log the request data
      const requestData = {
        login_identifier: loginIdentifier,
        old_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      };
      // Call change password API
      const response = await fetch(`${config.API_BASE_URL}/kirazee/api/v1/admin/auth/change-password/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (data.success) {
        // Close modal and reset form
        setShowChangePasswordModal(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setPasswordError('');

        // Show success message (no alert)
      } else {
        // Handle error messages
        let errorMessage = data.message || 'Password change failed';

        if (data.errors) {
          const errorMessages = [];
          Object.keys(data.errors).forEach(field => {
            if (data.errors[field] && data.errors[field].length > 0) {
              errorMessages.push(`${field}: ${data.errors[field].join(', ')}`);
            }
          });

          if (errorMessages.length > 0) {
            errorMessage = errorMessages.join('\n');
          }
        }

        setPasswordError(errorMessage);
      }
    } catch (error) {
      console.error('Change password error:', error);
      setPasswordError('Network error. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Get user token from localStorage
      const savedUser = localStorage.getItem('user');
      const userData = savedUser ? JSON.parse(savedUser) : null;
      const token = userData?.token;

      // Call logout API if token exists
      if (token) {
        const response = await fetch(`${config.API_BASE_URL}/kirazee/api/v1/admin/auth/logout/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          console.warn('Logout API call failed:', response.status);
        }
      }

      // Clear user data and reload page
      localStorage.removeItem('user');
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
      // Even if API call fails, clear local data and reload
      localStorage.removeItem('user');
      window.location.reload();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="user-dropdown" ref={dropdownRef}>
      <div
        className="dropdown-trigger-icon"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <FaUserCircle className="user-icon" />
        <FaCaretDown className={`dropdown-arrow ${isOpen ? 'open' : ''}`} />
      </div>

      {isOpen && (
        <div
          className="dropdown-menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 'auto',
            left: '-180px',
            background: 'white',
            border: '2px solid #F55D00',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            minWidth: '240px',
            zIndex: 9999
          }}
        >
          <div className="dropdown-header">
            <div className="user-avatar">
              <FaUserCircle />
            </div>
            <div className="user-details">
              <div className="user-display-name">{user?.name || 'User'}</div>
              <div className="user-role-badge">{user?.role || 'SuperAdmin'}</div>
            </div>
          </div>

          <div className="dropdown-divider"></div>

          <button className="dropdown-item change-password-item" onClick={(e) => {
            e.stopPropagation();
            setShowChangePasswordModal(true);
            setIsOpen(false);
          }}>
            <FaKey className="item-icon" />
            <span>Change Password</span>
          </button>

          <div className="dropdown-divider"></div>

          <button className="dropdown-item logout-item" onClick={handleLogout}>
            <FaSignOutAlt className="item-icon" />
            <span>Logout</span>
          </button>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div className="user-dropdown-modal-overlay">
          <div className="user-dropdown-change-password-modal">
            <div className="user-dropdown-modal-header">
              <h3>Change Password</h3>
              <button
                className="user-dropdown-close-btn"
                onClick={() => {
                  setShowChangePasswordModal(false);
                  setPasswordError('');
                  setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                }}
              >
                ×
              </button>
            </div>

            <div className="user-dropdown-modal-body">
              {passwordError && (
                <div className="user-dropdown-error-message">
                  {passwordError.split('\n').map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </div>
              )}

              <div className="user-dropdown-form-group">
                <label>Current Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords.currentPassword ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({
                      ...prev,
                      currentPassword: e.target.value
                    }))}
                    placeholder="Enter current password"
                    disabled={passwordLoading}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPasswords(prev => ({
                      ...prev,
                      currentPassword: !prev.currentPassword
                    }))}
                    disabled={passwordLoading}
                  >
                    {showPasswords.currentPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="user-dropdown-form-group">
                <label>New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords.newPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({
                      ...prev,
                      newPassword: e.target.value
                    }))}
                    placeholder="Enter new password"
                    disabled={passwordLoading}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPasswords(prev => ({
                      ...prev,
                      newPassword: !prev.newPassword
                    }))}
                    disabled={passwordLoading}
                  >
                    {showPasswords.newPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="user-dropdown-form-group">
                <label>Confirm New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords.confirmPassword ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({
                      ...prev,
                      confirmPassword: e.target.value
                    }))}
                    placeholder="Confirm new password"
                    disabled={passwordLoading}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPasswords(prev => ({
                      ...prev,
                      confirmPassword: !prev.confirmPassword
                    }))}
                    disabled={passwordLoading}
                  >
                    {showPasswords.confirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
            </div>

            <div className="user-dropdown-modal-footer">
              <button
                className="user-dropdown-cancel-btn"
                onClick={() => {
                  setShowChangePasswordModal(false);
                  setPasswordError('');
                  setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                }}
                disabled={passwordLoading}
              >
                Cancel
              </button>
              <button
                className="user-dropdown-submit-btn"
                onClick={handleChangePassword}
                disabled={passwordLoading}
              >
                {passwordLoading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;
